import React, { ComponentType, FC, useCallback, useMemo, useState } from 'react';
import { OMWidgetsConfigModel } from '@om-fe/utils';
import URLParse from 'url-parse';
import { MatchDetailsSlider } from '@om-fe/widgets/dist/widgets/match-details-slider/match-details-slider';
import { MatchDetailsSliderWidgetIds } from '@om-fe/widgets/dist/widgets/match-details-slider/match-details-slider.constants';
import { useLang } from '@om-fe/widgets/dist/common/i18n/i18n.hooks';
import { EventTimeModel, MatchDetailsViewModel } from '@om-fe/models';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { AppState } from '@om-fe/core/core/types';
import { BreakpointsState } from '@om-fe/core/core/store/breakpoints';
import { FALLBACK_LANGUAGE, I18nContextInterface } from '@om-fe/core/core/i18n/utils';
import getConfig from '@om-fe/core/utils/config';
import { dynamicDomain } from '@om-fe/core/utils/dynamic-domain';
import { getOMWidgetConfig } from '@om-fe/core/utils/config/om-widgets-config';
import { getBasePath, getParentHost } from '@om-fe/core/utils/iframe-query-params';
import {
    MatchDetailsStateExt,
    SLICE_MATCH_DETAILS
} from '@om-fe/core/components/match-details/match-details.store';
import getSlice from '@om-fe/core/utils/get-slice';
import { getCurrentVirtualState, setVirtualStateExtraParams, VirtualStateModel } from '@om-fe/core/core/routing/custom-routes/custom-routes.utils';

interface MatchDetailsSliderContainerPublicProps {
    readonly matchId: string;
    readonly tabsList: MatchDetailsSliderWidgetIds[];
    readonly disciplineId: string;
    readonly className?: string;
    readonly onTabChanged?: (active: MatchDetailsSliderWidgetIds) => void;
    readonly isLive?: boolean;
    readonly isRightSidebar?: boolean;
}

interface MatchDetailsSliderContainerStoreProps {
    readonly hostname: string | undefined;
    readonly event?: MatchDetailsViewModel | null;
    readonly eventTime?: EventTimeModel | null;
    readonly breakpoints: BreakpointsState;
    readonly isAuthenForTopic?: boolean;
}

type MatchDetailsSliderContainerProps = MatchDetailsSliderContainerPublicProps
    & MatchDetailsSliderContainerStoreProps
    & I18nContextInterface;

const MatchDetailsSliderContainerComponent: FC<MatchDetailsSliderContainerProps> = function MatchDetailsSliderContainerComponent(
    {
        matchId,
        hostname,
        event,
        eventTime,
        breakpoints,
        disciplineId,
        tabsList,
        selectedLanguage,
        className,
        onTabChanged,
        isLive,
        isRightSidebar,
        isAuthenForTopic,
    }: MatchDetailsSliderContainerProps
) {
    const {
        layout,
        betradar,
        statScoreWidgetConfig,
        statScoreWidgetV2Config,
        i18nConfig: { defaultLanguage },
        matchStatistics,
        virtualState: configVirtualState,
        stream,
        matchDetailsLiveEventHeaderDefaultTab,
    } = getConfig();

    const breakpointSmall: boolean = breakpoints.small;
    const virtualState: VirtualStateModel = getCurrentVirtualState();
    let defaultActiveTab: MatchDetailsSliderWidgetIds | undefined =
        betradar?.enabledRightSideBarLMT
            ? MatchDetailsSliderWidgetIds.LiveMatchTracker
            : statScoreWidgetV2Config?.group
                ? MatchDetailsSliderWidgetIds.StatscoreV2
                : undefined;
    if (
        configVirtualState &&
        virtualState &&
        virtualState.extraParams?.activeTab &&
        breakpointSmall
    ) {
        defaultActiveTab = virtualState.extraParams?.activeTab;
    }

    const lang = useLang();
    const clientId: string | undefined = betradar?.clientId;
    const [activeTab, setActiveTab] = useState<MatchDetailsSliderWidgetIds | undefined>(defaultActiveTab);
    const hasBetradarStatistics: boolean = !!matchStatistics;
    const omScoreWidgetConfigData: OMWidgetsConfigModel = getOMWidgetConfig(lang, hostname);
    const distUrl = useMemo<string>(() => {
        return dynamicDomain({ urlTemplate: omScoreWidgetConfigData.distUrl, hostname })
    }, [omScoreWidgetConfigData.distUrl, hostname]);
    const statScoreWidgetConfigId: string | undefined = statScoreWidgetConfig?.dataConfig;
    const parentHost: string | undefined = getParentHost();
    const basePath: string | undefined = getBasePath();
    const baseUrl: URLParse | undefined = !!basePath ? new URLParse(basePath) : undefined;
    const hideTimeOnHalfTime: boolean = !!layout?.hideTimeOnHalfTime;
    const logoPath: string | undefined = betradar && betradar.logo
        ? dynamicDomain({ urlTemplate: betradar.logo })
        : undefined;
    const pitchLogoPath: string = betradar && betradar.pitchLogo
        ? dynamicDomain({ urlTemplate: betradar.pitchLogo })
        : undefined;
    const goalBannerImagePath: string = betradar && betradar.goalBannerImage
        ? dynamicDomain({ urlTemplate: betradar.goalBannerImage })
        : undefined;
    const statScoreWidgetV2ConfigId: string | undefined =
        statScoreWidgetV2Config?.group
            ? isLive
                ? statScoreWidgetV2Config?.group?.live 
                : statScoreWidgetV2Config?.group?.pending
        : undefined;

    const handleTabChange = useCallback((activeTab: MatchDetailsSliderWidgetIds): void => {
        if (onTabChanged) {
            onTabChanged(activeTab);
        }
        setActiveTab(activeTab);
        setVirtualStateExtraParams({ activeTab: activeTab });
    }, []);

    const initialActiveTab: MatchDetailsSliderWidgetIds = MatchDetailsSliderWidgetIds.Visualization;

    return (
        <MatchDetailsSlider
            matchId={matchId}
            fallbackLanguage={FALLBACK_LANGUAGE}
            defaultLanguage={defaultLanguage}
            onActiveTabChange={handleTabChange}
            activeTab={activeTab}
            event={event || {
                eventId: matchId,
            }}
            eventTime={eventTime}
            clientId={clientId}
            hasBetradarStatistics={hasBetradarStatistics}
            distUrl={distUrl}
            breakpointSmall={breakpointSmall}
            operatorSpecificList={tabsList}
            showVisibilityToggle={false}
            selectedLanguage={selectedLanguage}
            statScoreWidgetConfigId={statScoreWidgetConfigId}
            statScoreWidgetV2ConfigId={statScoreWidgetV2ConfigId}
            statScoreWidgetV2Group={statScoreWidgetV2Config?.group ? true : false}
            isRightSidebar={isRightSidebar}
            baseUrl={baseUrl}
            parentHost={parentHost}
            omScoreWidgetConfigData={omScoreWidgetConfigData}
            className={className}
            hideTimeOnHalfTime={hideTimeOnHalfTime}
            disciplineId={disciplineId}
            showTabNameOnMobile
            logo={logoPath}
            pitchLogo={pitchLogoPath}
            goalBannerImage={goalBannerImagePath}
            initialActiveTab={initialActiveTab}
            streamMinimumBalance={stream?.minimumBalance}
            userRole={stream?.userRole}
            isAuthenForTopic={isAuthenForTopic}
            matchDetailsLiveEventHeaderDefaultTab={matchDetailsLiveEventHeaderDefaultTab}
        />
    )
}

export const MatchDetailsSliderContainer: ComponentType<MatchDetailsSliderContainerPublicProps> = compose(
    connect<MatchDetailsSliderContainerStoreProps, MatchDetailsSliderContainerPublicProps, undefined, AppState>(
        (state: AppState): MatchDetailsSliderContainerStoreProps => {
            const matchSlice: MatchDetailsStateExt | undefined = getSlice(state, SLICE_MATCH_DETAILS);
            const { breakpoints, config: { hostname } } = state;
            const { enabledTopicDelay } = getConfig();
            const isAuthenForTopic: boolean = !!(enabledTopicDelay && state.session?.info?.isAuthenticated);

            if (!matchSlice) {
                return {
                    hostname,
                    breakpoints,
                    isAuthenForTopic,
                }
            }

            const event: MatchDetailsViewModel | null = matchSlice?.event;
            const eventTime: EventTimeModel | null = matchSlice?.eventTime;

            return {
                hostname,
                event,
                eventTime,
                breakpoints,
                isAuthenForTopic,
            }
        }
    )
)(MatchDetailsSliderContainerComponent);
